
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MessageCircle,
  ArrowRight,
  ChevronDown,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// =====================================
// COUNTRY PHONE CODES
// =====================================

const COUNTRY_CODES = [
  { code: "+355", country: "Albania", flag: "🇦🇱" },
  { code: "+1", country: "USA / Canada", flag: "🇺🇸" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+34", country: "Spain", flag: "🇪🇸" },
  { code: "+31", country: "Netherlands", flag: "🇳🇱" },
  { code: "+32", country: "Belgium", flag: "🇧🇪" },
  { code: "+41", country: "Switzerland", flag: "🇨🇭" },
  { code: "+43", country: "Austria", flag: "🇦🇹" },
  { code: "+46", country: "Sweden", flag: "🇸🇪" },
  { code: "+47", country: "Norway", flag: "🇳🇴" },
  { code: "+45", country: "Denmark", flag: "🇩🇰" },
  { code: "+358", country: "Finland", flag: "🇫🇮" },
  { code: "+48", country: "Poland", flag: "🇵🇱" },
  { code: "+30", country: "Greece", flag: "🇬🇷" },
  { code: "+40", country: "Romania", flag: "🇷🇴" },
  { code: "+359", country: "Bulgaria", flag: "🇧🇬" },
  { code: "+381", country: "Serbia", flag: "🇷🇸" },
  { code: "+385", country: "Croatia", flag: "🇭🇷" },
  { code: "+386", country: "Slovenia", flag: "🇸🇮" },
  { code: "+382", country: "Montenegro", flag: "🇲🇪" },
  { code: "+389", country: "North Macedonia", flag: "🇲🇰" },
  { code: "+387", country: "Bosnia", flag: "🇧🇦" },
  { code: "+383", country: "Kosovo", flag: "🇽🇰" },
  { code: "+90", country: "Turkey", flag: "🇹🇷" },
  { code: "+357", country: "Cyprus", flag: "🇨🇾" },
  { code: "+972", country: "Israel", flag: "🇮🇱" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+64", country: "New Zealand", flag: "🇳🇿" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+55", country: "Brazil", flag: "🇧🇷" },
  { code: "+52", country: "Mexico", flag: "🇲🇽" },
  { code: "+27", country: "South Africa", flag: "🇿🇦" },
];

// =====================================
// REASONS FOR CONTACT
// =====================================

const INQUIRY_TYPES = [
  { id: "new-project", title: "New Project" },
  { id: "training", title: "Cybersecurity Training" },
  { id: "partnership", title: "Partnership" },
  {
    id: "soc-services",
    title: "SOC & Cybersecurity Services",
  },
  {
    id: "software-development",
    title: "Software Development",
  },
  {
    id: "ai-automation",
    title: "AI & Business Automation",
  },
  {
    id: "general-inquiry",
    title: "General Inquiry",
  },
];

// =====================================
// WHATSAPP
// =====================================

const WHATSAPP_URL =
  "https://wa.me/355682008275?text=" +
  encodeURIComponent(
    "Hello FrontSpace, I would like to get in touch."
  );

// =====================================
// INITIAL FORM
// =====================================

const INITIAL_FORM = {
  name: "",
  email: "",
  phoneCode: "+355",
  phone: "",
  message: "",
};

// =====================================
// CONTACT PAGE
// =====================================

export default function Contact() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [inquiryType, setInquiryType] = useState("");
  const [status, setStatus] = useState(null);

  // =====================================
  // HANDLE FORM CHANGES
  // =====================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // =====================================
  // SUBMIT FORM
  // =====================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (status === "sending") return;

    setStatus("sending");

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone:
          `${formData.phoneCode}${formData.phone.trim()}`,
        message: formData.message.trim(),
        interest: inquiryType,
        language: "en",
      };

      const response = await fetch(
        `${BACKEND_URL}/api/leads`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Submission failed: ${response.status}`
        );
      }

      setStatus("success");
      setFormData(INITIAL_FORM);
      setInquiryType("");
    } catch (error) {
      console.error("Contact form error:", error);
      setStatus("error");
    }
  };

  // =====================================
  // CONTACT DETAILS
  // =====================================

  const contactDetails = [
    {
      icon: MapPin,
      title: "Our Location",
      content: (
        <span>
          Kavaja Street, Deliorgj
          <br />
          Tirana 1001, Albania
        </span>
      ),
    },
    {
      icon: Phone,
      title: "Phone",
      content: (
        <a
          href="tel:+355682008275"
          className="hover:text-[#00F0FF] transition-colors"
        >
          +355 68 200 8275
        </a>
      ),
    },
    {
      icon: Mail,
      title: "Email",
      content: (
        <a
          href="mailto:office@frontspace.io"
          className="hover:text-[#00F0FF] transition-colors"
        >
          office@frontspace.io
        </a>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#050814] text-white overflow-x-hidden relative">

      {/* ===================================== */}
      {/* BACKGROUND */}
      {/* ===================================== */}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,240,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.025) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none opacity-20"
        style={{
          background:
            "radial-gradient(circle, rgba(0,240,255,0.25), transparent 70%)",
        }}
      />

      <div
        className="absolute top-40 left-0 w-[500px] h-[500px] pointer-events-none opacity-15"
        style={{
          background:
            "radial-gradient(circle, rgba(0,85,255,0.3), transparent 70%)",
        }}
      />

      {/* ===================================== */}
      {/* NAVBAR */}
      {/* ===================================== */}

      <Navbar />

      <main className="relative max-w-7xl mx-auto px-5 sm:px-6 pt-32 pb-24">

        {/* ===================================== */}
        {/* PAGE HEADING */}
        {/* ===================================== */}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5"
            style={{
              fontFamily: "Outfit, sans-serif",
            }}
          >
            Contact{" "}
            <span className="text-[#00F0FF]">
              Us
            </span>
          </h1>
        </motion.div>

        {/* ===================================== */}
        {/* MAIN CONTACT GRID */}
        {/* ===================================== */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 items-stretch">

          {/* ===================================== */}
          {/* LEFT CONTACT INFORMATION PANEL */}
          {/* ===================================== */}

          <motion.section
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative bg-[#0A1024]/90 border border-[#00F0FF]/30 rounded-2xl p-7 sm:p-9 overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#00F0FF]" />

            <div className="relative z-10">
              <p className="text-[#00F0FF] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                LET'S CONNECT
              </p>

              <h2
                className="text-2xl sm:text-3xl font-bold mb-5"
                style={{
                  fontFamily: "Outfit, sans-serif",
                }}
              >
                We're Here to{" "}
                <span className="text-[#00F0FF]">
                  Help
                </span>
              </h2>

              <p className="text-slate-400 leading-relaxed mb-10 max-w-md">
                Whether you have a project in mind,
                need information about our services,
                or want to explore a collaboration,
                our team is ready to assist you.
              </p>

              {/* WHATSAPP BUTTON */}

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 rounded-xl bg-[#25D366] px-6 py-3.5 font-bold text-[#05120A] hover:bg-[#46e482] transition-colors mb-9"
                aria-label="Chat with FrontSpace on WhatsApp"
              >
                <MessageCircle size={20} />
                Chat on WhatsApp
                <ArrowRight size={18} />
              </a>

              {/* CONTACT DETAILS */}

              <div className="space-y-8">
                {contactDetails.map((detail) => {
                  const Icon = detail.icon;

                  return (
                    <div
                      key={detail.title}
                      className="flex items-start gap-4"
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex-shrink-0">
                        <Icon
                          size={22}
                          className="text-[#00F0FF]"
                        />
                      </div>

                      <div>
                        <h3 className="text-white font-semibold mb-1">
                          {detail.title}
                        </h3>

                        <div className="text-slate-400 text-sm leading-relaxed">
                          {detail.content}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full pointer-events-none opacity-10"
              style={{
                background:
                  "radial-gradient(circle, #00F0FF, transparent 70%)",
              }}
            />
          </motion.section>

          {/* ===================================== */}
          {/* RIGHT CONTACT FORM PANEL */}
          {/* ===================================== */}

          <motion.section
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative bg-[#0A1024]/90 border border-[#00F0FF]/30 rounded-2xl p-7 sm:p-9 overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#00F0FF]" />

            <div className="relative z-10">
              <p className="text-[#00F0FF] text-xs font-bold uppercase tracking-[0.2em] mb-2">
                SEND US A MESSAGE
              </p>

              <h2
                className="text-2xl sm:text-3xl font-bold mb-7"
                style={{
                  fontFamily: "Outfit, sans-serif",
                }}
              >
                Get in Touch
              </h2>

              {/* ===================================== */}
              {/* SUCCESS MESSAGE */}
              {/* ===================================== */}

              {status === "success" ? (
                <div className="py-14 text-center">
                  <CheckCircle2
                    size={54}
                    className="text-[#00F0FF] mx-auto mb-5"
                  />

                  <h3 className="text-2xl font-bold mb-3">
                    Message Sent!
                  </h3>

                  <p className="text-slate-400 mb-7">
                    Thank you for contacting FrontSpace.
                    We'll get back to you as soon as possible.
                  </p>

                  <button
                    type="button"
                    onClick={() => setStatus(null)}
                    className="bg-[#00F0FF] text-[#050814] font-semibold px-6 py-3 rounded-lg hover:bg-white transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  data-testid="contact-form"
                >

                  {/* NAME AND EMAIL */}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-slate-300 text-sm font-medium mb-2"
                      >
                        Full Name *
                      </label>

                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        autoComplete="name"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-[#0D1930] border border-slate-600/60 rounded-lg px-4 py-3 text-white text-sm placeholder:text-slate-500 outline-none focus:border-[#00F0FF] transition-colors"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-slate-300 text-sm font-medium mb-2"
                      >
                        Email Address *
                      </label>

                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        autoComplete="email"
                        placeholder="Your email address"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-[#0D1930] border border-slate-600/60 rounded-lg px-4 py-3 text-white text-sm placeholder:text-slate-500 outline-none focus:border-[#00F0FF] transition-colors"
                      />
                    </div>

                  </div>

                  {/* ===================================== */}
                  {/* PHONE NUMBER */}
                  {/* ===================================== */}

                  <div>
                    <label
                      htmlFor="contact-phone"
                      className="block text-slate-300 text-sm font-medium mb-2"
                    >
                      Phone Number *
                    </label>

                    <div className="flex items-stretch border border-slate-600/60 rounded-lg overflow-hidden focus-within:border-[#00F0FF] transition-colors bg-[#0D1930]">

                      <div className="relative flex-shrink-0 border-r border-slate-600/60">

                        <select
                          name="phoneCode"
                          value={formData.phoneCode}
                          onChange={handleChange}
                          aria-label="Country calling code"
                          className="appearance-none bg-transparent text-slate-300 text-xs sm:text-sm pl-3 pr-8 py-3 outline-none cursor-pointer max-w-[175px]"
                        >
                          {COUNTRY_CODES.map((country) => (
                            <option
                              key={`${country.country}-${country.code}`}
                              value={country.code}
                              className="bg-[#0A1024] text-white"
                            >
                              {country.flag} {country.country} ({country.code})
                            </option>
                          ))}
                        </select>

                        <ChevronDown
                          size={14}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                        />
                      </div>

                      <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        required
                        autoComplete="tel-national"
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={handleChange}
                        className="flex-1 min-w-0 bg-transparent px-4 py-3 text-white text-sm placeholder:text-slate-500 outline-none"
                      />

                    </div>
                  </div>

                  {/* ===================================== */}
                  {/* REASON FOR CONTACT DROPDOWN */}
                  {/* ===================================== */}

                  <div>
                    <label
                      htmlFor="contact-reason"
                      className="block text-slate-300 text-sm font-medium mb-2"
                    >
                      Reason for Contact *
                    </label>

                    <div className="relative">
                      <select
                        id="contact-reason"
                        name="interest"
                        value={inquiryType}
                        onChange={(e) =>
                          setInquiryType(e.target.value)
                        }
                        required
                        className="w-full appearance-none bg-[#0D1930] border border-slate-600/60 rounded-lg px-4 pr-10 py-3 text-white text-sm outline-none focus:border-[#00F0FF] transition-colors cursor-pointer"
                      >
                        <option value="" disabled>
                          Select a reason for contacting us
                        </option>

                        {INQUIRY_TYPES.map((type) => (
                          <option
                            key={type.id}
                            value={type.id}
                            className="bg-[#0D1930] text-white"
                          >
                            {type.title}
                          </option>
                        ))}
                      </select>

                      <ChevronDown
                        size={16}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* ===================================== */}
                  {/* MESSAGE */}
                  {/* ===================================== */}

                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-slate-300 text-sm font-medium mb-2"
                    >
                      Your Message *
                    </label>

                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      required
                      placeholder="Tell us how we can help you..."
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full bg-[#0D1930] border border-slate-600/60 rounded-lg px-4 py-3 text-white text-sm placeholder:text-slate-500 outline-none focus:border-[#00F0FF] transition-colors resize-none"
                    />
                  </div>

                  {/* ===================================== */}
                  {/* ERROR MESSAGE */}
                  {/* ===================================== */}

                  {status === "error" && (
                    <div
                      role="alert"
                      className="flex items-center gap-2 text-red-400 text-sm"
                    >
                      <AlertCircle size={17} />

                      <span>
                        Your message could not be sent.
                        Please try again or email us directly.
                      </span>
                    </div>
                  )}

                  {/* ===================================== */}
                  {/* SUBMIT BUTTON */}
                  {/* ===================================== */}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full inline-flex items-center justify-center gap-3 bg-[#00F0FF] text-[#050814] font-bold px-6 py-3.5 rounded-lg hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2
                          size={18}
                          className="animate-spin"
                        />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={17} />
                        Send Message
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>

                </form>
              )}
            </div>
          </motion.section>

        </div>
      </main>

      <Footer />
    </div>
  );
}
